'use client';

import React, { useCallback } from 'react';
import { trackClick } from '@/lib/analytics'; // Import the unified trackClick function

interface AnalyticsClickWrapperProps {
  children: React.ReactNode;
  // You can add optional props here if you want to pass context from the parent
  // For example, a section name for all clicks within this wrapper
  contextCategory?: string;
  label?: string; // Optional ID to track specific elements
}

/**
 * AnalyticsClickWrapper Component
 * This is a client component that captures click events from its children
 * (which can be server-rendered content) and sends them to the unified analytics tracker.
 * It uses event delegation to efficiently track clicks on multiple elements.
 */
export default function AnalyticsClickWrapper({ children, contextCategory, label }: AnalyticsClickWrapperProps) {
  const handleClick = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    // Determine the actual clicked element that is a button or has a clear ID
    const target = event.target as HTMLElement;

    // Traverse up the DOM tree to find the closest clickable element with an ID or specific tag
    let clickableElement: HTMLElement | null = target;
    while (clickableElement && clickableElement !== event.currentTarget) {
      if (clickableElement.tagName === 'BUTTON' || clickableElement.id) {
        break; // Found a button or an element with an ID
      }
      clickableElement = clickableElement.parentElement;
    }

    if (clickableElement && clickableElement !== event.currentTarget) {
      const elementId = label || clickableElement.id || clickableElement.tagName.toLowerCase();
      const elementText = clickableElement.textContent?.trim() || '';
      const category = contextCategory || 'Interaction'; // Use contextCategory if provided, else default
      const action = 'Click';

      // Call the unified analytics trackClick function
      trackClick(elementId, category, action, label);

      console.log(`AnalyticsClickWrapper: Captured click on ID: ${elementId}, Text: "${elementText}"`);
    }
  }, [contextCategory]);

  return (
    // Attach the onClick listener to the wrapper div.
    // Clicks on any child element will bubble up to this handler.
    <div onClick={handleClick}>
      {children}
    </div>
  );
}
